import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import PhotoMedia from "@/components/PhotoMedia";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, ImageIcon, Play, Trash2, ExternalLink, LayoutGrid, Share2, Globe } from "lucide-react";
import { toast } from "sonner";

const CreatorWall = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [counts, setCounts] = useState({ photos: 0, videos: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/wall/me");
      setPosts(data.posts);
      setCounts(data.counts);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const removePost = async (id) => {
    try {
      await api.delete(`/wall/${id}`);
      toast.success("Publication supprimée");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const shareVitrine = async () => {
    const url = `${window.location.origin}/c/${user?.username}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `MIHOD PAY — @${user?.username}`, text: "Découvrez mon mur MIHOD PAY", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Lien de la vitrine copié !");
      }
    } catch (e) {
      try { await navigator.clipboard.writeText(url); toast.success("Lien copié !"); } catch (_) {}
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl flex items-center gap-2"><LayoutGrid className="h-6 w-6 text-primary" /> {t("wall.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5"><Globe className="h-4 w-4" /> Publications publiques &amp; gratuites</p>
        </div>
      </div>

      {/* counts */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3 rounded-2xl text-center">
          <p className="text-[11px] text-muted-foreground">{t("wall.posts")}</p>
          <p className="font-display text-lg" data-testid="wall-count-total">{counts.total}</p>
        </Card>
        <Card className="p-3 rounded-2xl text-center">
          <p className="text-[11px] text-muted-foreground">{t("profile.photosPublished")}</p>
          <p className="font-display text-lg" data-testid="wall-count-photos">{counts.photos}</p>
        </Card>
        <Card className="p-3 rounded-2xl text-center">
          <p className="text-[11px] text-muted-foreground">{t("profile.videosPublished")}</p>
          <p className="font-display text-lg" data-testid="wall-count-videos">{counts.videos}</p>
        </Card>
      </div>

      <div className="flex gap-2">
        <Button className="flex-1 rounded-xl neon-glow-blue-sm" onClick={() => navigate("/envoyer")} data-testid="wall-new-post-button">
          <Plus className="h-4 w-4 mr-1" /> {t("wall.publish")}
        </Button>
        <Button variant="outline" className="rounded-xl" onClick={() => navigate(`/c/${user?.username}`)} data-testid="wall-view-vitrine">
          <ExternalLink className="h-4 w-4 mr-1" /> {t("wall.visitVitrine")}
        </Button>
        <Button variant="outline" size="icon" className="rounded-xl shrink-0" onClick={shareVitrine} data-testid="wall-share-button" aria-label="Partager">
          <Share2 className="h-4 w-4" />
        </Button>
      </div>

      {/* posts grid */}
      {loading ? (
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="aspect-[4/5] rounded-2xl" />)}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3"><ImageIcon className="h-6 w-6" /></div>
          <p className="font-medium text-foreground">{t("wall.empty")}</p>
          <p className="text-sm mt-1">{t("wall.emptyHint")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3" data-testid="wall-posts-grid">
          {posts.map((p) => (
            <div key={p.id} className="rounded-2xl overflow-hidden border border-border bg-card relative">
              <div className="relative aspect-[4/5]">
                <PhotoMedia photo={{ ...p, is_sender: true }} />
                <button
                  onClick={() => removePost(p.id)}
                  className="absolute top-2 right-2 h-8 w-8 rounded-lg bg-card/90 grid place-items-center hover:bg-destructive/10"
                  data-testid={`wall-delete-${p.id}`}
                  aria-label="Supprimer"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </button>
                {p.media_type === "video" && (
                  <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[10px] text-white">
                    <Play className="h-3 w-3 fill-white" /> {t("common.video")}
                  </div>
                )}
              </div>
              <div className="p-2.5">
                <p className="text-xs truncate">{p.caption || "—"}</p>
                <p className="text-[11px] font-medium text-[hsl(160_84%_38%)] flex items-center gap-1"><Globe className="h-3 w-3" /> Public · Gratuit</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreatorWall;
